import React, { useMemo, useState } from 'react';
import { Database, CheckCircle, AlertTriangle, Layers, GitBranch, Tag, Zap, BarChart3, FileJson, ChevronDown, ChevronRight, AlertOctagon } from 'lucide-react';
import { validateSeedCoverage, validateSeedQuestionsLocal, getAllSeedQuestions, getSeedQuestionTypeDistribution, SEED_COLLECTIONS, getSeedCollection } from '../../../data/seedConsolidator';
import { TAXONOMY, FAMILIES_BY_MODULE } from '../../../config/taxonomyRegistry';
import { MODULES } from '../../../data/modules';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { ModuleId } from '../../../types';
export function SeedStatus() {
  const [expandedModule, setExpandedModule] = useState<ModuleId | null>(null);
  const [expandedQuestion, setExpandedQuestion] = useState<string | null>(null);
  const allQuestions = useMemo(() => getAllSeedQuestions(), []);
  const coverageReport = useMemo(() => validateSeedCoverage(), []);
  const validationReport = useMemo(() => validateSeedQuestionsLocal(), []);
  const typeDistribution = useMemo(() => getSeedQuestionTypeDistribution(), []);
  const coveragePercent = Math.round(coverageReport.coveredFamilies / coverageReport.totalFamilies * 100);
  return <div className="space-y-8" data-id="element-1795">
      {/* Top Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-1796">
        <StatCard label="Total Seed Questions" value={allQuestions.length} icon={Database} color="indigo" data-id="element-1797" />

        <StatCard label="Taxonomy Coverage" value={`${coveragePercent}%`} icon={GitBranch} color={coveragePercent === 100 ? 'emerald' : 'amber'} data-id="element-1798" />

        <StatCard label="Family Coverage" value={`${coverageReport.coveredFamilies}/${coverageReport.totalFamilies}`} icon={Tag} color="blue" data-id="element-1799" />

        <StatCard label="Question Types" value={Object.keys(typeDistribution).length} icon={Layers} color="purple" data-id="element-1800" />

      </div>

      {/* Coverage Status Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1801">
        <Card className="p-6 lg:col-span-2" data-id="element-1802">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1803">
            <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-1804" />
            Coverage Status
          </h3>

          <div className="mb-8" data-id="element-1805">
            <div className="flex justify-between items-end mb-2" data-id="element-1806">
              <span className="text-sm font-medium text-slate-600" data-id="element-1807">
                Overall Progress
              </span>
              <span className={`text-2xl font-bold ${coveragePercent === 100 ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-1808">

                {coveragePercent}%
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden" data-id="element-1809">
              <div className={`h-full transition-all duration-500 ${coveragePercent === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
              width: `${coveragePercent}%`
            }} data-id="element-1810" />

            </div>
          </div>

          {coverageReport.missingFamilies.length > 0 ? <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4" data-id="element-1811">
              <h4 className="text-sm font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-1812">
                <AlertTriangle className="w-4 h-4" data-id="element-1813" />
                Missing Families ({coverageReport.missingFamilies.length})
              </h4>
              <div className="space-y-2 max-h-60 overflow-y-auto pr-2" data-id="element-1814">
                {coverageReport.missingFamilies.map(id => <div key={id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-100 text-sm" data-id="element-1815">

                    <span className="font-mono text-amber-900" data-id="element-1816">{id}</span>
                  </div>)}
              </div>
            </div> : <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 flex items-center gap-3" data-id="element-1817">
              <div className="bg-emerald-100 p-2 rounded-full" data-id="element-1818">
                <CheckCircle className="w-5 h-5 text-emerald-600" data-id="element-1819" />
              </div>
              <div data-id="element-1820">
                <div className="font-bold text-emerald-900" data-id="element-1821">
                  100% Coverage Achieved
                </div>
                <div className="text-sm text-emerald-700" data-id="element-1822">
                  All {coverageReport.totalFamilies} families from the taxonomy
                  have a corresponding seed question.
                </div>
              </div>
            </div>}
        </Card>

        {/* Question Type Distribution */}
        <Card className="p-6" data-id="element-1823">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-1824">
            <Layers className="w-5 h-5 text-purple-600" data-id="element-1825" />
            Question Types
          </h3>

          <div className="space-y-4" data-id="element-1826">
            <TypeRow label="Multiple Choice" count={typeDistribution['multiple_choice'] || 0} required data-id="element-1827" />

            <div className="h-px bg-slate-100 my-2" data-id="element-1828" />
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1829">
              Interactive Types
            </div>

            <TypeRow label="Klok (Clock)" count={typeDistribution['clock'] || 0} required data-id="element-1830" />

            <TypeRow label="Geld (Money)" count={typeDistribution['money'] || 0} required data-id="element-1831" />

            <TypeRow label="Cijferend Optellen" count={typeDistribution['cijferend_optellen'] || 0} required data-id="element-1832" />

            <TypeRow label="Invulvraag" count={typeDistribution['fill_in'] || 0} required data-id="element-1833" />

            <TypeRow label="Tabel Lezen" count={typeDistribution['table_reading'] || 0} required data-id="element-1834" />

          </div>
        </Card>
      </div>

      {/* Per-Module Breakdown */}
      <div data-id="element-1835">
        <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1836">
          <BarChart3 className="w-5 h-5 text-blue-600" data-id="element-1837" />
          Module Breakdown
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1838">
          {MODULES.map(module => {
          const collection = SEED_COLLECTIONS.get(module.id);
          const families = FAMILIES_BY_MODULE.get(module.id) || [];
          const questionCount = collection?.questions.length || 0;
          const familyCount = families.length;
          const coverage = familyCount > 0 ? Math.round(questionCount / familyCount * 100) : 0;
          const tiers = collection?.questions.map(q => q.tier).filter(Boolean) || [];
          const minTier = tiers.length > 0 ? Math.min(...tiers) : 0;
          const maxTier = tiers.length > 0 ? Math.max(...tiers) : 0;
          return <Card key={module.id} className="p-4 border-l-4" style={{
            borderLeftColor: getModuleColor(module.id)
          }} data-id="element-1839">

                <div className="flex justify-between items-start mb-2" data-id="element-1840">
                  <h4 className="font-bold text-slate-900" data-id="element-1841">{module.title}</h4>
                  <Badge variant="neutral" size="sm" data-id="element-1842">
                    {collection?.moduleCode}
                  </Badge>
                </div>

                <div className="space-y-3 mt-4" data-id="element-1843">
                  <div className="flex justify-between text-sm" data-id="element-1844">
                    <span className="text-slate-500" data-id="element-1845">Questions</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-1846">
                      {questionCount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1847">
                    <span className="text-slate-500" data-id="element-1848">Families</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-1849">
                      {familyCount}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm" data-id="element-1850">
                    <span className="text-slate-500" data-id="element-1851">Tier Range</span>
                    <span className="font-mono font-bold text-slate-700" data-id="element-1852">
                      {minTier}-{maxTier}
                    </span>
                  </div>

                  <div className="pt-2" data-id="element-1853">
                    <div className="flex justify-between text-xs mb-1" data-id="element-1854">
                      <span className="text-slate-400" data-id="element-1855">Coverage</span>
                      <span className={coverage === 100 ? 'text-emerald-600 font-bold' : 'text-amber-600 font-bold'} data-id="element-1856">

                        {coverage}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-1857">
                      <div className={`h-full rounded-full ${coverage === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{
                    width: `${coverage}%`
                  }} data-id="element-1858" />

                    </div>
                  </div>
                </div>
              </Card>;
        })}
        </div>
      </div>

      {/* Validation Results */}
      <Card className="p-6" data-id="element-1859">
        <div className="flex items-center justify-between mb-6" data-id="element-1860">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2" data-id="element-1861">
            <Zap className="w-5 h-5 text-amber-500" data-id="element-1862" />
            Validation Results
          </h3>
          <div className="flex gap-2" data-id="element-1863">
            <Badge variant="success" size="lg" data-id="element-1864">
              {validationReport.valid} Valid
            </Badge>
            {validationReport.invalid > 0 && <Badge variant="error" size="lg" data-id="element-1865">
                {validationReport.invalid} Invalid
              </Badge>}
          </div>
        </div>

        {validationReport.issues.length > 0 ? <div className="space-y-2" data-id="element-1866">
            {validationReport.issues.map(issue => <div key={issue.id} className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-id="element-1867">

                <div className="font-mono text-sm font-bold text-rose-800 mb-1" data-id="element-1868">
                  {issue.id}
                </div>
                <ul className="list-disc list-inside text-sm text-rose-700" data-id="element-1869">
                  {issue.problems.map((p, i) => <li key={i} data-id="element-1870">{p}</li>)}
                </ul>
              </div>)}
          </div> : <div className="text-center py-8 bg-slate-50 rounded-xl border border-dashed border-slate-200" data-id="element-1871">
            <CheckCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-1872" />
            <p className="text-slate-500 font-medium" data-id="element-1873">
              All seed questions passed validation
            </p>
          </div>}
      </Card>
    </div>;
}
function StatCard({
  label,
  value,
  icon: Icon,
  color
}: any) {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600'
  };
  return <Card className="p-4 flex items-center justify-between" data-id="element-1874">
      <div data-id="element-1875">
        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1" data-id="element-1876">
          {label}
        </div>
        <div className="text-2xl font-bold text-slate-900" data-id="element-1877">{value}</div>
      </div>
      <div className={`p-3 rounded-full ${colorClasses[color as keyof typeof colorClasses]}`} data-id="element-1878">

        <Icon className="w-6 h-6" data-id="element-1879" />
      </div>
    </Card>;
}
function TypeRow({
  label,
  count,
  required
}: any) {
  return <div className="flex items-center justify-between" data-id="element-1880">
      <div className="flex items-center gap-2" data-id="element-1881">
        {count > 0 ? <CheckCircle className="w-4 h-4 text-emerald-500" data-id="element-1882" /> : required ? <AlertTriangle className="w-4 h-4 text-rose-500" data-id="element-1883" /> : <div className="w-4 h-4" data-id="element-1884" />}
        <span className="text-sm font-medium text-slate-700" data-id="element-1885">{label}</span>
      </div>
      <Badge variant={count > 0 ? 'neutral' : 'warning'} size="sm" data-id="element-1886">
        {count}
      </Badge>
    </div>;
}
function getModuleColor(moduleId: ModuleId): string {
  const colors: Record<ModuleId, string> = {
    Metriek: '#3b82f6',
    Getalbegrip: '#6366f1',
    TijdGeld: '#10b981',
    SpellingWW: '#f43f5e',
    SpellingRegel: '#f59e0b',
    Taalbegrip: '#a855f7',
    Studievaardig: '#14b8a6',
    Topo: '#06b6d4'
  };
  return colors[moduleId] || '#64748b';
}